import React from 'react';
import { useAuthenticated } from '@gettactic/hooks';
import { Image } from '@v2/components/display/Image/Image';
import { TacticIcon } from '@cmp-src/icons/TacticLogo/TacticIcon';
import { getOrganizationUrl } from '@gettactic/helpers/src/url/getOrganizationUrl';
import { ScrollArea } from '@v2/components/ui/scroll-area';
import { cn } from '@gettactic/helpers/src';
import { CloseIcon } from '@v2/icons';
import { useCloseAnimationTimeout } from '@gettactic/helpers/src/hooks/useCloseAnimationTimeout';
import { useGlobalEscape } from '@gettactic/helpers/src/hooks/useGlobalEscape';
import { Link } from '@v2/components/ui/Link';

export interface OrganizationSwitcherProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const Classes = {
  Overlay: cn(
    'fixed inset-0 z-modal bg-black/50 backdrop-blur-sm ',
    'data-[state=open]:fade-in-0 data-[state=open]:animate-in',
    'data-[state=closed]:fade-out-0 data-[state=closed]:animate-out'
  ),
  Panel: cn(
    'fixed top-0 bottom-0 left-0 z-modal flex w-full md:max-w-[500px] flex-col bg-white shadow-xl',
    'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:fade-in-0 data-[state=open]:animate-in',
    'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out'
  ),
  Header: 'flex items-center justify-between border-gray-200 px-7 pt-6',
  CloseButton: 'p-4',
  CloseIcon: 'size-4',
  ScrollContent: 'flex flex-col gap-6 p-6',
  OrgButton:
    'relative flex min-h-20 cursor-pointer items-center gap-4 rounded-xl border border-gray-200 p-4 hover:bg-gray-50',
  LogoImage: 'h-auto w-10',
  LogoIcon: 'size-10'
};

export const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({
  isOpen,
  onOpenChange
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  useGlobalEscape(() => {
    onOpenChange(false);
  });

  const { isVisible } = useCloseAnimationTimeout(isOpen);

  if (!isVisible) return null;

  return (
    <div>
      <div
        className={Classes.Overlay}
        data-state={isOpen ? 'open' : 'closed'}
        onClick={() => onOpenChange(false)}
      />
      <div data-state={isOpen ? 'open' : 'closed'} className={Classes.Panel}>
        <div className={Classes.Header}>
          <h2 className="font-semibold text-lg">Switch Organization</h2>
          <button
            className={Classes.CloseButton}
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className={Classes.CloseIcon} />
          </button>
        </div>
        <ScrollArea className="w-full flex-1">
          <div className={Classes.ScrollContent}>
            {(authenticatedUser.organizations || []).map((organization) => (
              <Link
                key={organization.id}
                className={Classes.OrgButton}
                href={getOrganizationUrl(organization, '/dashboard')}
              >
                <p className="text-gray-500 text-sm">
                  {organization.logo_url ? (
                    <Image
                      src={organization.logo_url}
                      className={Classes.LogoImage}
                    />
                  ) : (
                    <TacticIcon className={Classes.LogoIcon} />
                  )}
                </p>
                <h3 className="font-semibold text-lg">{organization.name}</h3>
              </Link>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
